import axios, { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import ab2str from 'arraybuffer-to-string';
import config from '../../config';
import JWTService from './JWTService';
import showMessage, { convertUtcDateInString } from '../components/Toastr/ToastService';
import { getLanguage } from './UserService';
import { clearLocalStoragePreservingAppKeys } from '../components/BESwitcher/BESwitcherStorage';

export interface IHttpServiceOptions {
  showToaster?: boolean; // Show toaster for HTTP status 20x
  showErrorToaster?: boolean | number[]; // Show toaster for HTTP status > 40x or define HTTP status code
  returnAxiosError?: boolean; // Returns the whole Axios error response istead of undefined when error occurs
  returnAxioErrorResponseData?: boolean; // Returns only response data from Axios Error when error occurs
  requestTimeout?: number;
  baseUrl?: string;
  headers?: AxiosRequestHeaders;
  noAuthHeader?: boolean; // Do not send Authorisation header with JWT in request
  returnAxiosResponse?: boolean; // Returns full success Axios response with headers not only data
  queryParams?: Record<string, string>;
  data?: any;
  signal?: AbortSignal;
}

export const STATUS_ERROR = 'error';
export const STATUS_WARNING = 'warning';

export const STATUS_NOT_FOUND = 404;
export const STATUS_APP_ERROR = 500;

export const RESPONSE_STATUS_ERROR = 'error';
export const RESPONSE_STATUS_OK = 'ok';

export const STATUS_OK = 'ok';
export const RESPONSE_OK = 'ok';
export const RESPONSE_ERROR = 'error';
export const IS_OK = 200;
export const IS_BAD_REQUEST = 400;

export const AXIOS_BASE_URL = `${config.Backend.url}/api`;
export const AXIOS_TIMEOUT = 90000;
export const AXIOS_HEADERS = {
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
  'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
  'Content-Type': 'application/json',
  'Access-Control-Expose-Headers': 'Content-Disposition',
};
const ERR_NETWORK = 'ERR_NETWORK';
const ERR_CANCELED = 'ERR_CANCELED';
const ACCESS_TOKEN_EXPIRED = 'ACCESS_TOKEN_EXPIRED';
const ACL_ROLE_NOT_FOUND = 'ACL_ROLE_NOT_FOUND';

// Keeps application preferences while removing authentication data.
export const clearAuthStorage = (): void => {
  clearLocalStoragePreservingAppKeys();
};

// const handleUnauthorized = (error: any): void => {
//   if (
//     error?.response?.status === 401 &&
//     error.response.data?.message === ACCESS_TOKEN_EXPIRED &&
//     !String(error.config?.url || '').includes('v1/auth/sign-in')
//   ) {
//     clearAuthStorage();
//     window.location.href = '/';
//   }
// };

const isRequestCanceled = (error: any): boolean =>
  error?.code === ERR_CANCELED ||
  error?.name === 'CanceledError' ||
  axios.isCancel?.(error) === true;

const requestConfigSettings = (options: IHttpServiceOptions): AxiosRequestConfig => {
  const requestConfig: AxiosRequestConfig = {
    baseURL: options.baseUrl ? options.baseUrl : AXIOS_BASE_URL,
    timeout: options.requestTimeout ? options.requestTimeout : AXIOS_TIMEOUT,
    headers: options.headers
      ? { ...options.headers, Authorization: JWTService.getToken() }
      : {
          ...AXIOS_HEADERS,
          'Accept-Language': getLanguage() || 'en',
          Authorization: JWTService.getToken(),
        },
  };

  if (options.headers && options.headers.Authorization && options.noAuthHeader) {
    delete options.headers.Authorization;
  }

  if (options.signal) {
    requestConfig.signal = options.signal;
  }

  return requestConfig;
};

const handleSuccessResponseToast = (response: AxiosResponse) => {
  if (response.data.toast) {
    showMessage(
      '',
      convertUtcDateInString(response.data.toast.message),
      response.data.toast.type.toLowerCase(),
      response.data.toast.duration,
      response.data.toast.messagePayload || null
    );
  }
  return response;
};

const detailed500Error = (error: any) => {
  if (error.code !== ERR_NETWORK) {
    const additionalMessage =
      config.apiService.detailed500Error && error.response.data && error.response.data.message
        ? error.response.data.message
        : null;

    let errorMessage = 'Unexpected Api Error';
    if (error.response.data && error.response.data.toast && error.response.data.toast.message) {
      errorMessage = error.response.data.toast.message;
    }
    if (additionalMessage) {
      errorMessage = `Unexpected Api Error ${additionalMessage}`;
    }
    if (error.response.data.toast) {
      showMessage(
        '',
        errorMessage,
        error.response.data.toast.type.toLowerCase(),
        error.response.data.toast.duration,
        error.response.data.toast.messagePayload || null
      );
    } else {
      showMessage('', errorMessage, 'error', null, null);
    }
  }
};

const handleErrorResponseToast = (error: AxiosError) => {
  if (isRequestCanceled(error)) {
    return Promise.reject(error);
  }
  if (!error.response && error.code !== ERR_NETWORK) {
    // No response from server
    showMessage('', 'Unexpected API Error', 'error');
  }
  // if (error.response && error.response.status === STATUS_NOT_FOUND) {
  //   showMessage('', 'Endpoint not found.', 'error');
  //   return error;
  // }
  console.info('HTTPService error: ', error);
  detailed500Error(error);
  return Promise.reject(error);
  // return error;
};

const createQueryParamsFromObject = (object: Record<string, any>): string => {
  const params = new URLSearchParams();
  Object.keys(object).forEach((queryItem: string) => {
    if (object[queryItem] && !Array.isArray(object[queryItem])) {
      params.append(queryItem, object[queryItem]);
    }
    if (object[queryItem] && Array.isArray(object[queryItem])) {
      // console.log('setting: ', queryItem, object[queryItem]);
      object[queryItem].forEach((value: string) => {
        params.append(`${queryItem}[]`, value);
      });
    }
  });
  // console.log('params: ', params.toString());
  return params.toString();
};

export async function getRequest<T>(
  url: string,
  options?: IHttpServiceOptions
): Promise<T | AxiosResponse<unknown, any> | false> {
  let opts: IHttpServiceOptions = { showToaster: true, showErrorToaster: true };
  if (options) {
    opts = { ...opts, ...options };
  }

  const requestConfig: AxiosRequestConfig = requestConfigSettings(opts);

  if (opts.noAuthHeader && requestConfig.headers) {
    delete requestConfig.headers.Authorization;
  }

  const instance = axios.create(requestConfig);

  if (opts.showToaster) {
    instance.interceptors.response.use(handleSuccessResponseToast);
  }

  if (opts.showErrorToaster) {
    instance.interceptors.response.use(undefined, handleErrorResponseToast);
  }

  // instance.interceptors.request.use(
  //   function (response) {
  //     // Any status code that lie within the range of 2xx cause this function to trigger
  //     // Do something with response data
  //     return response;
  //   }, function (error) {
  //     // Any status codes that falls outside the range of 2xx cause this function to trigger
  //     // Do something with response error
  //     return Promise.reject(error);
  //   }
  // );

  let urlWithParams = url;
  if (opts.queryParams) {
    urlWithParams = `${url}?${createQueryParamsFromObject(opts.queryParams)}`;
  }

  return instance
    .get(urlWithParams)
    .then((response: AxiosResponse) => {
      if (opts.returnAxiosResponse) {
        return response;
      }
      const responseData: T = response.data;
      return responseData;
    })
    .catch((error) => {
      // Let react-query / callers observe aborts; do not map them to `false`.
      if (opts.signal?.aborted || isRequestCanceled(error)) {
        throw error;
      }
      // handleUnauthorized(error);
      if (
        Array.isArray(error.config?.showErrorToaster) &&
        error.config?.showErrorToaster.length > 0
      ) {
        if (error.config?.showErrorToaster.includes(error.response.status)) {
          detailed500Error(error);
        }
      }
      if (error.config?.showErrorToaster === true) {
        detailed500Error(error);
      }
      // Add interceptor for custom error handling
      if (opts.returnAxiosError) {
        return error.response;
      }
      if (opts.returnAxioErrorResponseData) {
        return error.response.data;
      }
      return false;
    });
}

export async function postRequest<D, T>(
  url: string,
  data: D,
  options: IHttpServiceOptions = { showToaster: true, showErrorToaster: true }
): Promise<T | false> {
  let opts: IHttpServiceOptions = { showToaster: true, showErrorToaster: true };
  if (options) {
    opts = { ...opts, ...options };
  }

  const requestConfig: AxiosRequestConfig = requestConfigSettings(opts);

  if (opts.noAuthHeader && requestConfig.headers) {
    delete requestConfig.headers.Authorization;
  }

  const instance = axios.create(requestConfig);

  if (opts.showToaster) {
    instance.interceptors.response.use(handleSuccessResponseToast);
  }

  if (opts.showErrorToaster) {
    instance.interceptors.response.use(undefined, handleErrorResponseToast);
  }

  let urlWithParams = url;
  if (opts.queryParams) {
    urlWithParams = `${url}?${createQueryParamsFromObject(opts.queryParams)}`;
  }

  return instance
    .post(urlWithParams, data)
    .then((response: AxiosResponse) => {
      if (opts.returnAxiosResponse) {
        return response;
      }
      const responseData: T = response.data;
      return responseData;
    })
    .catch((error) => {
      // handleUnauthorized(error);
      // Add interceptor for custom error handling
      if (opts.returnAxiosError) {
        return error.response;
      }
      if (opts.returnAxioErrorResponseData) {
        return error.response.data;
      }
      return false;
    });
}

export async function putRequest<D, T>(
  url: string,
  data: D,
  options: IHttpServiceOptions = { showToaster: true, showErrorToaster: true }
): Promise<T | false> {
  let opts: IHttpServiceOptions = { showToaster: true, showErrorToaster: true };
  if (options) {
    opts = { ...opts, ...options };
  }

  const requestConfig: AxiosRequestConfig = requestConfigSettings(opts);

  if (opts.noAuthHeader && requestConfig.headers) {
    delete requestConfig.headers.Authorization;
  }

  const instance = axios.create(requestConfig);

  if (opts.showToaster) {
    instance.interceptors.response.use(handleSuccessResponseToast);
  }

  if (opts.showErrorToaster) {
    instance.interceptors.response.use(undefined, handleErrorResponseToast);
  }

  let urlWithParams = url;
  if (opts.queryParams) {
    urlWithParams = `${url}?${createQueryParamsFromObject(opts.queryParams)}`;
  }

  return instance
    .put(urlWithParams, data)
    .then((response: AxiosResponse) => {
      if (opts.returnAxiosResponse) {
        return response;
      }
      const responseData: T = response.data;
      return responseData;
    })
    .catch((error) => {
      // handleUnauthorized(error);
      // Add interceptor for custom error handling
      if (opts.returnAxiosError) {
        return error.response;
      }
      if (opts.returnAxioErrorResponseData) {
        return error.response.data;
      }
      return false;
    });
}

export async function patchRequest<D, T>(
  url: string,
  data: D,
  options: IHttpServiceOptions = { showToaster: true, showErrorToaster: true }
): Promise<T | false> {
  let opts: IHttpServiceOptions = { showToaster: true, showErrorToaster: true };
  if (options) {
    opts = { ...opts, ...options };
  }

  const requestConfig: AxiosRequestConfig = requestConfigSettings(opts);

  if (opts.noAuthHeader && requestConfig.headers) {
    delete requestConfig.headers.Authorization;
  }

  const instance = axios.create(requestConfig);

  if (opts.showToaster) {
    instance.interceptors.response.use(handleSuccessResponseToast);
  }

  if (opts.showErrorToaster) {
    instance.interceptors.response.use(undefined, handleErrorResponseToast);
  }

  let urlWithParams = url;
  if (opts.queryParams) {
    urlWithParams = `${url}?${createQueryParamsFromObject(opts.queryParams)}`;
  }

  return instance
    .patch(urlWithParams, data)
    .then((response: AxiosResponse) => {
      if (opts.returnAxiosResponse) {
        return response;
      }
      const responseData: T = response.data;
      return responseData;
    })
    .catch((error) => {
      // handleUnauthorized(error);
      // Add interceptor for custom error handling
      if (opts.returnAxiosError) {
        return error.response;
      }
      if (opts.returnAxioErrorResponseData) {
        return error.response.data;
      }
      return false;
    });
}

export async function deleteRequest<T>(
  url: string,
  options: IHttpServiceOptions = { showToaster: true, showErrorToaster: true }
): Promise<T | false> {
  let opts: IHttpServiceOptions = { showToaster: true, showErrorToaster: true };
  if (options) {
    opts = { ...opts, ...options };
  }

  const requestConfig: AxiosRequestConfig = requestConfigSettings(opts);

  if (opts.noAuthHeader && requestConfig.headers) {
    delete requestConfig.headers.Authorization;
  }

  const instance = axios.create(requestConfig);

  if (opts.showToaster) {
    instance.interceptors.response.use(handleSuccessResponseToast);
  }

  if (opts.showErrorToaster) {
    instance.interceptors.response.use(undefined, handleErrorResponseToast);
  }

  let urlWithParams = url;
  if (opts.queryParams) {
    urlWithParams = `${url}?${createQueryParamsFromObject(opts.queryParams)}`;
  }

  return instance
    .delete(urlWithParams, opts)
    .then((response: AxiosResponse) => {
      if (opts.returnAxiosResponse) {
        return response;
      }
      const responseData: T = response.data;
      return responseData;
    })
    .catch((error) => {
      // handleUnauthorized(error);
      // Add interceptor for custom error handling
      if (opts.returnAxiosError) {
        return error.response;
      }
      if (opts.returnAxioErrorResponseData) {
        return error.response.data;
      }
      return false;
    });
}

const binaryConfig = (mimeType?: string): AxiosRequestConfig => ({
  // Downloads hit the backend root, not the /api prefix used by JSON requests
  baseURL: config.Backend.url,
  timeout: AXIOS_TIMEOUT,
  responseType: 'arraybuffer',
  headers: {
    ...AXIOS_HEADERS,
    'Accept-Language': getLanguage() || 'en',
    Authorization: JWTService.getToken(),
    ...(mimeType ? { Accept: mimeType } : {}),
  },
});

// Faithful port of ApiService.upload: multipart POST that always resolves with
// the AxiosResponse (or error.response), showing the backend toast when present.
export async function uploadRequest(
  url: string,
  data: any = null,
  enableMessage = true
): Promise<AxiosResponse> {
  const axiosConfig: AxiosRequestConfig = {
    baseURL: AXIOS_BASE_URL,
    timeout: AXIOS_TIMEOUT,
    headers: {
      ...AXIOS_HEADERS,
      'Accept-Language': getLanguage() || 'en',
      Authorization: JWTService.getToken(),
    },
  };
  if (axiosConfig.headers) {
    delete axiosConfig.headers['Content-Type'];
  }

  const instance = axios.create(axiosConfig);
  const formData = new FormData();
  formData.append('file', data);
  return instance
    .post(url, formData)
    .then((response) => {
      if (enableMessage && response.data.toast) {
        showMessage(
          '',
          response.data.toast.message,
          response.data.toast.type.toLowerCase(),
          response.data.toast.duration,
          response.data.toast.messagePayload || null
        );
      }
      return response;
    })
    .catch((error) => {
      if (
        error.response?.status === 500 &&
        error.response.data.message &&
        error.response.data.message === ACL_ROLE_NOT_FOUND
      ) {
        showMessage('', 'ACL Role not found.', 'error');
        return error;
      }
      if (!error.response) {
        if (enableMessage) {
          showMessage('', 'Unexpected Api Error', 'error');
        }
        return error;
      }
      if (error.response.status === 400 && !error.response.data.form) {
        if (error.response.data.toast.message) {
          showMessage(
            '',
            error.response.data.toast.message,
            'warning',
            error.response.data.toast.duration,
            error.response.data.toast.messagePayload || null
          );
        } else {
          showMessage('', 'Undefined Error Message', 'warning');
        }
      } else if (
        error.response.status === 400 &&
        error.response.data.form &&
        error.response.data.form.validation &&
        !error.response.data.form.validation.passed
      ) {
        if (error.response.data.toast.message) {
          showMessage(
            '',
            error.response.data.toast.message,
            'warning',
            error.response.data.toast.duration,
            error.response.data.toast.messagePayload || null
          );
        } else {
          showMessage('', 'Undefined Error Message', 'warning');
        }
      } else if (error.response.status === 401 && !error.response.data.toast) {
        showMessage('', 'Unauthorized', 'error');
      } else if (error.response.status === 401 && error.response.data.toast) {
        showMessage('', error.response.data.toast.message, 'warning');
      } else if (error.response.status === 403) {
        if (error.response.data.toast.message) {
          showMessage(
            '',
            error.response.data.toast.message,
            'error',
            error.response.data.toast.duration,
            error.response.data.toast.messagePayload || null
          );
        } else {
          showMessage('', 'Forbidden', 'error');
        }
      } else if (error.response.status === 404) {
        showMessage('', error.response.data.toast.message, 'warning');
      } else {
        showMessage('', 'Unexpected Api Error', 'error');
      }
      return error.response;
    });
}

// Faithful port of ApiService.postDownload: arraybuffer POST for exports.
export async function postDownloadRequest(
  url: string,
  mimeType: string,
  body: Record<any, any>
): Promise<AxiosResponse> {
  const instance = axios.create(binaryConfig(mimeType));
  return instance
    .post(url, body)
    .then((response: AxiosResponse) => {
      if (response.data.toast) {
        showMessage(
          '',
          response.data.toast.message,
          response.data.toast.type.toLowerCase(),
          response.data.toast.duration,
          response.data.toast.messagePayload || null
        );
      }
      return response;
    })
    .catch((error) => {
      const data = JSON.parse(ab2str(error.response.data));
      if (error.response.status === 401) {
        if (data.message === ACCESS_TOKEN_EXPIRED) {
          clearAuthStorage();
          window.location.href = '/';
        }
        showMessage('', 'Unauthorized', 'error');
      } else if (error.response.status === 403) {
        if (data.toast.message) {
          showMessage(
            '',
            data.toast.message,
            'error',
            data.toast.duration,
            data.toast.messagePayload || null
          );
        } else {
          showMessage('', 'Forbidden', 'error');
        }
      } else if (error.response.status === 404) {
        showMessage('', data.toast.message, 'warning');
      } else if (error.response.status === 400 && data.passed === undefined) {
        if (data.toast) {
          showMessage(
            '',
            data.toast.message,
            'warning',
            data.toast.duration,
            data.toast.messagePayload || null
          );
        } else {
          showMessage('', data.toast.message, 'warning', data.toast.duration);
        }
      } else {
        showMessage('', data.toast.message, 'error', data.toast.duration);
      }
      return error.response;
    });
}

// Faithful port of ApiService.download: arraybuffer GET with optional abort signal.
export async function downloadRequest(
  url: string,
  mimeType: string,
  signal?: AbortSignal | undefined
): Promise<AxiosResponse> {
  const instance = axios.create(binaryConfig(mimeType));
  return instance
    .get(url, { signal })
    .then((response) => {
      if (response.data.toast) {
        showMessage(
          '',
          response.data.toast.message,
          response.data.toast.type.toLowerCase(),
          response.data.toast.duration,
          response.data.toast.messagePayload || null
        );
      }
      return response;
    })
    .catch((error) => {
      if (!error.response && !signal) {
        showMessage('', 'Unexpected API Error', 'error');
        return error;
      }
      if (error.response.status === 401) {
        if (error.response.data.message === ACCESS_TOKEN_EXPIRED) {
          clearAuthStorage();
          window.location.href = '/';
        }
        showMessage('', 'Unauthorized', 'error');
      } else if (error.response.status === 403) {
        if (error.response.data.toast.message) {
          showMessage(
            '',
            error.response.data.toast.message,
            'error',
            error.response.data.toast.duration,
            error.response.data.toast.messagePayload || null
          );
        } else {
          showMessage('', 'Forbidden', 'error');
        }
      } else if (error.response.status === 404) {
        if (error.response.data.toast) {
          showMessage('', error.response.data.toast.message, 'warning');
        } else {
          showMessage('', 'Unexpected API Error', 'error');
          return false;
        }
      } else {
        const data = JSON.parse(ab2str(error.response.data));
        showMessage('', data.toast?.message || 'Unexpected API Error', 'error');
      }
      return error.response;
    });
}
